import React, { useState, useEffect } from 'react';
import { 
  Building2, Search, Users, Award, ExternalLink,
  Globe, CheckCircle, Heart, Share2, Eye, Edit2, Trash2,
  Plus, X, ToggleLeft, ToggleRight, RefreshCw
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDirectoryContent = () => {
  const [organizations, setOrganizations] = useState([]);
  const [serviceSubmissions, setServiceSubmissions] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('directory');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSize, setFilterSize] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchOrganizations();
    fetchServiceSubmissions();
    fetchIndustries();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/directory`);
      const data = await res.json();
      setOrganizations(data.directory || []);
    } catch (err) {
      console.error('Failed to fetch directory:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceSubmissions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/directory/submissions`);
      const data = await res.json();
      setServiceSubmissions(data.submissions || []);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    }
  };

  const fetchIndustries = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/industries`);
      const data = await res.json();
      setIndustries(data.industries || []);
    } catch (err) {
      console.error('Failed to fetch industries:', err);
    }
  };

  const handleApprove = async (submissionId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/directory/submissions/${submissionId}/approve`, { method: 'PUT' });
      fetchServiceSubmissions();
    } catch (err) {
      console.error('Failed to approve:', err);
    }
  };

  const handleReject = async (submissionId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/directory/submissions/${submissionId}/reject`, { method: 'PUT' });
      fetchServiceSubmissions();
    } catch (err) {
      console.error('Failed to reject:', err);
    }
  };

  const handleToggleActive = async (submissionId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/directory/submissions/${submissionId}/toggle`, { method: 'PATCH' });
      fetchServiceSubmissions();
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  const pendingSubmissions = serviceSubmissions.filter(s => s.status === 'pending');
  const approvedSubmissions = serviceSubmissions.filter(s => s.status === 'approved' || s.status === 'published');

  const filteredOrgs = organizations
    .filter(o => filterSize === 'all' || o.organization_size === filterSize)
    .filter(o => 
      o.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.industry?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const sizeLabels = {
    MICRO: '1-10 employees',
    SMALL: '11-50 employees',
    MEDIUM: '51-250 employees',
    LARGE: '251-1000 employees',
    ENTERPRISE: '1000+ employees'
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-directory">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Directory Management</h1>
        <p className="text-gray-600">Manage certified organizations and service listings</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-700">{organizations.length}</div>
          <div className="text-sm text-blue-600">Certified Organizations</div>
        </div>
        <div className="bg-amber-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-amber-700">{pendingSubmissions.length}</div>
          <div className="text-sm text-amber-600">Pending Service Reviews</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-700">{approvedSubmissions.length}</div>
          <div className="text-sm text-green-600">Published Services</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-700">{industries.length}</div>
          <div className="text-sm text-purple-600">Industries</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('directory')}
          className={`pb-3 px-4 font-semibold border-b-2 ${
            activeTab === 'directory'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500'
          }`}
          data-testid="tab-directory"
        >
          Certified Directory ({organizations.length})
        </button>
        <button
          onClick={() => setActiveTab('submissions')}
          className={`pb-3 px-4 font-semibold border-b-2 flex items-center gap-2 ${
            activeTab === 'submissions'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500'
          }`}
          data-testid="tab-submissions"
        >
          Service Submissions
          {pendingSubmissions.length > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {pendingSubmissions.length}
            </span>
          )}
        </button>
      </div>

      {/* Directory Tab */}
      {activeTab === 'directory' && (
        <div>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search organizations..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <select
              value={filterSize}
              onChange={(e) => setFilterSize(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Sizes</option>
              <option value="MICRO">Micro</option>
              <option value="SMALL">Small</option>
              <option value="MEDIUM">Medium</option>
              <option value="LARGE">Large</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
            <span className="text-gray-600">{filteredOrgs.length} organizations</span>
          </div>

          {/* Organizations Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Industry</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certification</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certified Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrgs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No certified organizations yet</p>
                    </td>
                  </tr>
                ) : (
                  filteredOrgs.map((org) => (
                    <tr key={org.id || org.organization_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-indigo-600" />
                          </div>
                          <span className="font-medium text-gray-900">{org.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {org.organization_size}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {org.industry || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          org.certification_tier === 'Luminary' ? 'bg-amber-100 text-amber-700' :
                          org.certification_tier === 'Inspired' ? 'bg-purple-100 text-purple-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {org.certification_tier || 'Aspiring'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {org.certified_at ? new Date(org.certified_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Submissions for Review</h3>
          
          {serviceSubmissions.length === 0 ? (
            <div className="bg-white rounded-lg border p-12 text-center">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No service submissions yet</p>
              <p className="text-sm text-gray-400 mt-2">
                Organizations can submit their services once they're certified
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {serviceSubmissions.map((submission) => (
                <div key={submission.id} className="bg-white rounded-lg border p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{submission.service_name}</h4>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          submission.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          submission.status === 'approved' || submission.status === 'published' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {submission.status}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{submission.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {submission.organization_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          {submission.industry || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {submission.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(submission.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Approve"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleReject(submission.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Reject"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      {(submission.status === 'approved' || submission.status === 'published') && (
                        <button
                          onClick={() => handleToggleActive(submission.id)}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                          title="Toggle Active"
                        >
                          {submission.active !== false ? (
                            <ToggleRight className="w-5 h-5 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Wrapper with SystemAdminLayout
const AdminDirectoryPage = () => {
  return (
    <SystemAdminLayout>
      <AdminDirectoryContent />
    </SystemAdminLayout>
  );
};

export default AdminDirectoryPage;
